import * as React from "react";
import Typography from "@mui/material/Typography";
import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useTheme} from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ProfilePictureSelect from "../common/ProfileSelect/button";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import ProfileService from "../api/profile";
import {useSelector, useDispatch} from "react-redux";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import CustomTextField from "../common/CustomTextField";
import SelectFields from "../common/selectFields";
import {showMessage} from "../store/messageSlice";
import {validateEmail} from "../utils/systemInfo";
import FakeUser from "../FakeUser.png";
import Breadcrumbs from "../common/breadcrumbs";
import OrganizationsMenu from "../common/Layout/organizationsMenu";

const breadcrumbs = [
    {id: 0, name: "Home", href: "./"},
    {id: 1, name: "Users", href: "users"},
    {id: 2, name: "Create User", href: "userCreate"}
];
export default function CreateUser() {
    const dispatch = useDispatch();
    const [userType, setUserType] = useState();
    const [userTypes, setUserTypes] = useState([]);
    const [name, setName] = useState("");
    const [lName, setLName] = useState("");
    const [pass, setPass] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const profilePicRef = React.useRef();
    const [profilePicFile, setProfilePicFile] = useState(null);
    // const [profilePicFile, setProfilePicFile] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formError, setFormError] = useState("");

    const navigate = useNavigate();
    const organziation = useSelector(({organziation}) => organziation);
    const orgObj = organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
    const apiKey = orgObj?.api_key_list?.[0]?.key;
    const [orgApiKey, setOrgApiKey] = useState(apiKey);
    const [orgId, setOrgId] = useState(orgObj.uid);
    const theme = useTheme();
    const {t} = useTranslation();

    useEffect(() => {
        ProfileService.GetUserType({
            user_type_uid: "",
            name: "",
            status: "active",
            apiKey: apiKey
        }).then((json) => {
            if (json.app_user_type !== undefined && json.app_user_type.length > 0) {
                let userTypesJson = json.app_user_type;
                let userTypesArr = [];
                for (let i in userTypesJson) {
                    userTypesArr.push({
                        value: userTypesJson[i].user_type_uid,
                        label: userTypesJson[i].name
                    });
                }
                setUserTypes(userTypesArr);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function validateForm() {
        if (!name?.trim()) {
            setFormError("First name is required.");
            return false;
        }
        if (!pass) {
            setFormError("Password is required.");
            return false;
        }
        if (!userType) {
            setFormError("User type is required.");
            return false;
        }
        if (!email && !mobile) {
            setFormError("At least one contact (email or mobile) is required.");
            return false;
        }
        if (email && !validateEmail(email)) {
            setFormError("Email is not valid.");
            return false;
        }
        setFormError("");
        return true;
    }

    function showErrorMessage(msg) {
        dispatch(
            showMessage({
                message: t("error occurred"),
                autoHideDuration: 10000,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "center"
                },
                variant: "error"
            })
        );
        if (msg) setFormError(msg);
    }

    function getImageObject() {
        return new Promise((resolve) => {
            if (!profilePicFile) {
                resolve(null);
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64String = event.target.result.split(",")[1];
                const fileExt = profilePicFile.name.split(".").pop().toLowerCase();
                const imageObject = {
                    image_type: "profile_image",
                    encoding: "base64",
                    status: "active",
                    mime: fileExt,
                    value: base64String
                };
                resolve(imageObject);
            };
            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                resolve(null);
            };
            reader.readAsDataURL(profilePicFile);
        });
    }

    const handleSave = async () => {
        if (!validateForm()) return;
        setSaveLoading(true);
        setFormError("");

        const image = await getImageObject();
        ProfileService.CreateUser({
            firstname: name,
            lastname: lName,
            email,
            mobile,
            user_type_uid: userType,
            password: pass,
            image,
            apiKey: orgApiKey
        })
            .then((json) => {
                if (json.success && json.count) {
                    dispatch(
                        showMessage({
                            message: t("successfully saved"), //text or html
                            autoHideDuration: 30000, //ms
                            anchorOrigin: {
                                vertical: "top", //top bottom
                                horizontal: "center" //left center right
                            },
                            variant: "success" //success error info warning null
                        })
                    );
                    navigate("/users");
                } else {
                    showErrorMessage(json?.msg || t("error occurred"));
                }
                setSaveLoading(false);
            })
            .catch(() => {
                dispatch(
                    showMessage({
                        message: t("error occurred"), //text or html
                        autoHideDuration: 10000, //ms
                        anchorOrigin: {
                            vertical: "top", //top bottom
                            horizontal: "center" //left center right
                        },
                        variant: "error" //success error info warning null
                    })
                );
                setSaveLoading(false);
            });
    };

    const handleCancel = () => navigate("/users");
    const handleUserTypeChange = (value) => setUserType(value);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();
    const handleMouseUpPassword = (event) => event.preventDefault();

    return (
        <Box sx={{p: "12px", height: "100%"}}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box sx={{display: {xs: "block", sm: "flex"}}}>
                <Typography
                    sx={{
                        fontSize: "45px",
                        mb: 0,
                        color: theme.palette.custom.text
                    }}
                    variant="h2"
                    gutterBottom>
                    {t("Createuser")}
                </Typography>
            </Box>
            <Divider sx={{my: "10px", backgroundColor: theme.palette.custom.borderColor}} />

            {formError && (
                <Typography color="error" sx={{mb: 2}}>
                    {formError}
                </Typography>
            )}

            <Box
                id="profileBox"
                sx={{
                    display: {xs: "block", sm: "flex"},
                    width: "100%",
                    flexDirection: "row",
                    color: "#FFFFFF"
                }}>
                <Box
                    sx={{
                        display: {xs: "block", sm: "flex"},
                        flexDirection: "row",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center"
                    }}>
                    <Box
                        sx={{
                            display: {xs: "block", sm: "flex"},
                            flexDirection: "column"
                        }}>
                        <ProfilePictureSelect ref={profilePicRef} header="Change Profile Picture" label="Profile Picture" url={FakeUser} onFileChange={(file) => setProfilePicFile(file)} />
                    </Box>
                    <Box
                        sx={{
                            display: {xs: "block", sm: "flex"},
                            flexDirection: "column",
                            width: "100%",
                            marginLeft: ".5rem",
                            marginRight: ".5rem"
                        }}>
                        <Box sx={{height: "80px"}}>
                            <CustomTextField
                                id="txtFirstName"
                                label={t("Firstname")}
                                variant="filled"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                inputProps={{maxLength: 32}}
                                sx={{
                                    width: {xs: "100%", sm: "100%"},
                                    borderColor: theme.palette.custom.borderColor,
                                    borderRadius: "11px",
                                    backgroundColor: theme.palette.custom.light
                                }}
                            />
                        </Box>
                        <Box sx={{height: "80px"}}>
                            <CustomTextField
                                id="txtLastName"
                                label={t("Lastname")}
                                variant="filled"
                                value={lName}
                                inputProps={{maxLength: 32}}
                                onChange={(e) => setLName(e.target.value)}
                                sx={{
                                    width: {xs: "100%", sm: "100%%"},
                                    borderColor: theme.palette.custom.borderColor,
                                    borderRadius: "11px",
                                    backgroundColor: theme.palette.custom.light,
                                    marginTop: {xs: "8px", sm: "0px"}
                                }}
                            />
                        </Box>
                        <Box sx={{height: "80px"}}>
                            <CustomTextField
                                id="txtMobile"
                                label={t("Mobile Number")}
                                variant="filled"
                                value={mobile}
                                type="tel"
                                inputProps={{maxLength: 32}}
                                onChange={(e) => setMobile(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+</InputAdornment>
                                }}
                                sx={{
                                    width: {xs: "100%", sm: "100%%"},
                                    borderColor: theme.palette.custom.borderColor,
                                    borderRadius: "11px",
                                    backgroundColor: theme.palette.custom.light,
                                    marginTop: {xs: "8px", sm: "0px"}
                                }}
                            />
                        </Box>
                        <OrganizationsMenu
                            tooltip={t("Organization")}
                            userListOnly
                            operationId={orgId}
                            onChange={(id, org) => {
                                setOrgId(id);
                                setOrgApiKey(org?.api_key_list?.[0]?.key);
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: {xs: "block", sm: "flex"},
                            flexDirection: "column",
                            width: "100%",
                            marginLeft: ".5rem",
                            marginRight: ".5rem",
                            alignSelf: "flex-start"
                        }}>
                        <Box sx={{height: "80px"}}>
                            <CustomTextField
                                id="txtEmail"
                                label={t("Email Address")}
                                variant="filled"
                                value={email}
                                type="email"
                                error={email && !validateEmail(email)}
                                helperText={email && !validateEmail(email) ? t("Email not Valid") : null}
                                InputProps={{
                                    maxLength: 64
                                }}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={{
                                    width: {xs: "100%", sm: "100%%"},
                                    borderColor: theme.palette.custom.borderColor,
                                    borderRadius: "11px",
                                    backgroundColor: theme.palette.custom.light
                                }}
                            />
                        </Box>
                        <Box sx={{height: "80px"}}>
                            <CustomTextField
                                id="txtPass"
                                label={t("Password")}
                                variant="filled"
                                type={showPassword ? "text" : "password"}
                                value={pass}
                                onChange={(e) => setPass(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} onMouseUp={handleMouseUpPassword} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    width: {xs: "100%", sm: "100%%"},
                                    borderColor: theme.palette.custom.borderColor,
                                    borderRadius: "11px",
                                    backgroundColor: theme.palette.custom.light,
                                    marginTop: {xs: "8px", sm: "0px"}
                                }}
                            />
                        </Box>
                        <Box sx={{height: "80px"}}>
                            <SelectFields title={t("User Type")} value={userType} fields={userTypes} onChange={handleUserTypeChange} sx={{marginTop: {xs: "8px", sm: "0px"}}} />
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                    mt: 1
                }}>
                <Button
                    variant="contained"
                    sx={{
                        "backgroundColor": "red",
                        "color": "white",
                        "&:hover": {backgroundColor: "red"}
                    }}
                    onClick={handleCancel}>
                    {t("Cancel")}
                </Button>
                <LoadingButton
                    loading={saveLoading}
                    loadingIndicator={<CircularProgress color="primary" size={16} />}
                    type="submit"
                    variant="contained"
                    sx={{
                        "mt": 1,
                        "mb": 1,
                        "backgroundColor": theme.palette.custom.borderColor,
                        "color": "white",
                        "&:hover": {backgroundColor: theme.palette.custom.borderColor}
                    }}
                    onClick={handleSave}>
                    {t("CreateUser")}
                </LoadingButton>
            </Box>
            <Divider sx={{my: "10px", backgroundColor: theme.palette.custom.borderColor}} />
        </Box>
    );
}
